import { useState } from 'react';
import { Button, Divider, Group, Paper, Select, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconDatabase } from '@tabler/icons-react';
import { useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { CaspioCrmPanel } from './caspio-crm-panel';
import { CaspioCrmSetupModal } from './caspio-crm-setup-modal';
import { crmTypeLabels } from './crm-config';
import { PlatformCrmPanel } from './platform-crm-panel';
import { PlatformCrmSetupModal } from './platform-crm-setup-modal';
import { SystemOrganizationsWithCrmSubscription } from './graphql';
import type { SystemOrganization } from './graphql';

type CrmIntegration = NonNullable<SystemOrganization['crmIntegration']>;

function CopyCrmForm({ organizationId }: { organizationId: string }) {
  const [sourceOrganizationId, setSourceOrganizationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCopying, setIsCopying] = useState(false);

  const [{ data }] = useSubscription({ query: SystemOrganizationsWithCrmSubscription });

  const sources = (data?.organizations ?? []).filter((o) => o.id !== organizationId);

  const copy = async () => {
    if (!sourceOrganizationId) return;

    setError(null);
    setIsCopying(true);

    const { error: apiError } = await api
      .organizations({ organizationId })
      ['crm-integration'].copy.post({ sourceOrganizationId });

    setIsCopying(false);

    if (apiError) setError(getErrorMessage(apiError, 'Failed to copy the CRM integration'));
  };

  if (sources.length === 0) return null;

  return (
    <>
      <Divider label="or" labelPosition="center" w="100%" />
      <ErrorAlert message={error} />
      <Group gap="sm">
        <Select
          placeholder="Copy from organization..."
          data={sources.map((o) => ({ value: o.id, label: o.name }))}
          value={sourceOrganizationId}
          onChange={setSourceOrganizationId}
          searchable
          w={250}
        />
        <Button variant="light" onClick={() => void copy()} loading={isCopying} disabled={!sourceOrganizationId}>
          Copy CRM Setup
        </Button>
      </Group>
    </>
  );
}

function CrmSetupPanel({ organization }: { organization: SystemOrganization }) {
  const [isSettingUpCaspio, setIsSettingUpCaspio] = useState(false);
  const [isSettingUpPlatform, setIsSettingUpPlatform] = useState(false);

  return (
    <Paper p="xl" withBorder radius="md">
      <Stack align="center" gap="md">
        <ThemeIcon size={48} radius="xl" variant="light" color="gray">
          <IconDatabase size={24} />
        </ThemeIcon>
        <Stack gap={2} align="center">
          <Text fw={500}>No CRM integration</Text>
          <Text size="sm" c="dimmed">
            Connect a CRM to enable customer data lookup during calls.
          </Text>
        </Stack>
        <Group gap="sm">
          <Button variant="light" leftSection={<IconDatabase size={16} />} onClick={() => setIsSettingUpCaspio(true)}>
            Set Up {crmTypeLabels.caspio} Integration
          </Button>
          <Button variant="light" leftSection={<IconDatabase size={16} />} onClick={() => setIsSettingUpPlatform(true)}>
            Set Up {crmTypeLabels.platform} Integration
          </Button>
        </Group>
        <CopyCrmForm organizationId={organization.id} />
      </Stack>

      {isSettingUpCaspio && (
        <CaspioCrmSetupModal organization={organization} onClose={() => setIsSettingUpCaspio(false)} />
      )}

      {isSettingUpPlatform && (
        <PlatformCrmSetupModal organization={organization} onClose={() => setIsSettingUpPlatform(false)} />
      )}
    </Paper>
  );
}

function CrmEditorPanel({ organizationId, integration }: { organizationId: string; integration: CrmIntegration }) {
  if (integration.type === 'platform')
    return <PlatformCrmPanel organizationId={organizationId} integration={integration} />;

  return <CaspioCrmPanel organizationId={organizationId} integration={integration} />;
}

export function CrmPanel({ organization }: { organization: SystemOrganization }) {
  if (!organization.crmIntegration) return <CrmSetupPanel organization={organization} />;

  return (
    <CrmEditorPanel
      key={organization.crmIntegration.id}
      organizationId={organization.id}
      integration={organization.crmIntegration}
    />
  );
}
